import React from 'react';

// ── The Embrace Mark ────────────────────────────────────────────────
// Two arcs reaching toward each other. One is the patient (bold, full
// opacity). One is the family (softer, waiting). The gap between them
// is the problem. ClariMe is the answer.

const EmbraceMark = ({ className = 'w-9 h-9', id = 'em' }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <rect x="2" y="2" width="96" height="96" rx="22" fill={`url(#${id}-grad)`} />
    {/* Patient arc — bright, the protagonist */}
    <path
      d="M30 68C18 54 20 34 32 22C42 12 58 12 66 22"
      stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.95"
    />
    {/* Family arc — softer, the support */}
    <path
      d="M70 32C82 46 80 66 68 78C58 88 42 88 34 78"
      stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.45"
    />
    {/* Patient dot — bright, present */}
    <circle cx="66" cy="22" r="5.5" fill="white" />
    {/* Family dot — softer, waiting */}
    <circle cx="34" cy="78" r="5.5" fill="white" opacity="0.45" />
    <defs>
      <linearGradient id={`${id}-grad`} x1="2" y1="2" x2="98" y2="98" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0d9488" />
        <stop offset="0.5" stopColor="#14b8a6" />
        <stop offset="1" stopColor="#38d9c7" />
      </linearGradient>
    </defs>
  </svg>
);

// ── Small / simplified mark for tight spaces ────────────────────────
const EmbraceMarkSmall = ({ className = 'w-6 h-6', id = 'ems' }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <rect x="2" y="2" width="96" height="96" rx="22" fill={`url(#${id}-grad)`} />
    <path
      d="M30 66C20 54 22 36 32 24C42 14 56 14 64 24"
      stroke="white" strokeWidth="9" strokeLinecap="round" fill="none" opacity="0.95"
    />
    <path
      d="M70 34C80 46 78 64 68 76C58 86 44 86 36 76"
      stroke="white" strokeWidth="9" strokeLinecap="round" fill="none" opacity="0.4"
    />
    <circle cx="64" cy="24" r="9" fill="white" />
    <defs>
      <linearGradient id={`${id}-grad`} x1="2" y1="2" x2="98" y2="98" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0d9488" />
        <stop offset="0.5" stopColor="#14b8a6" />
        <stop offset="1" stopColor="#38d9c7" />
      </linearGradient>
    </defs>
  </svg>
);

// ── Logo lockup: mark + wordmark ────────────────────────────────────
const ClariMeLogo = ({ size = 'default', variant = 'light' }) => {
  const meColor = variant === 'dark' ? 'text-white' : 'text-slate-800';
  const clariColor = variant === 'dark' ? 'text-teal-300' : 'text-teal-600';

  const sizes = {
    small:   { icon: 'w-7 h-7',  text: 'text-xl',       gap: 'gap-2',   weight: 'font-normal' },
    default: { icon: 'w-9 h-9',  text: 'text-[1.65rem]', gap: 'gap-2.5', weight: 'font-normal' },
    large:   { icon: 'w-11 h-11', text: 'text-4xl',       gap: 'gap-3',   weight: 'font-normal' },
  };
  const s = sizes[size];
  const markId = `logo-${size}-${variant}`;

  return (
    <div className={`flex items-center ${s.gap}`}>
      <EmbraceMark className={s.icon} id={markId} />
      <span className={`${s.text} tracking-[-0.02em] leading-none`} style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}>
        <span className={`${s.weight} ${clariColor}`}>clari</span>
        <span className={`font-semibold ${meColor}`}>me</span>
      </span>
    </div>
  );
};

export default ClariMeLogo;
export { EmbraceMark, EmbraceMarkSmall };
